import { c as _c } from "react/compiler-runtime";
import { Tooltip } from "react-tooltip";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import type { RatingsSectionProps, RatingsData } from "./types";
import VoteBreakdownTooltip from "./tooltips/VoteBreakdownTooltip";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { formatApprovalCountdownTime } from "@/helpers/waves/approve-wave.helpers";
import { useApprovalDropStatus } from "@/hooks/waves/useApprovalDropStatus";
interface ParticipationDropRatingsTotalSectionProps extends RatingsSectionProps {
  readonly ratingsData: RatingsData;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
}
export default function ParticipationDropRatingsTotalSection(t0) {
  const $ = _c(43);
  const {
    drop,
    ratingsData,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t1
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const {
    currentRating
  } = ratingsData;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalValueClass = currentRating < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const displayWinningThreshold = typeof winningThreshold === "number" && winningThreshold > 0 ? winningThreshold : null;
  const hasWinningThreshold = displayWinningThreshold !== null;
  let t2;
  if ($[0] !== displayWinningThreshold || $[1] !== drop || $[2] !== isVotingClosed || $[3] !== winningThresholdMinDurationMs) {
    t2 = {
      drop,
      isClosed: isVotingClosed,
      winningThreshold: displayWinningThreshold,
      winningThresholdMinDurationMs
    };
    $[0] = displayWinningThreshold;
    $[1] = drop;
    $[2] = isVotingClosed;
    $[3] = winningThresholdMinDurationMs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const approvalStatus = useApprovalDropStatus(t2);
  const approvalStatusKind = approvalStatus.kind;
  let approvalStatusClass;
  if (approvalStatusKind === "approved" || approvalStatusKind === "approving" || approvalStatusKind === "reached_threshold") {
    approvalStatusClass = "tw-text-emerald-400";
  } else {
    if (approvalStatusKind === "closed") {
      approvalStatusClass = "tw-text-amber-300";
    }
  }
  let t3;
  bb0: {
    if (!hasWinningThreshold) {
      t3 = null;
      break bb0;
    }
    if (approvalStatus.kind === "approved") {
      t3 = "Approved";
      break bb0;
    }
    if (approvalStatus.kind === "approving") {
      const t4 = approvalStatus.countdownMs ?? 0;
      let t5;
      if ($[5] !== t4) {
        t5 = formatApprovalCountdownTime(t4);
        $[5] = t4;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t3 = `Approving in ${t5}`;
      break bb0;
    }
    if (approvalStatus.kind === "reached_threshold") {
      t3 = "Reached threshold";
      break bb0;
    }
    if (approvalStatus.kind === "closed") {
      t3 = "Closed";
      break bb0;
    }
    const t4 = approvalStatus.remaining ?? 0;
    let t5;
    if ($[7] !== t4) {
      t5 = formatNumberWithCommas(t4);
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t3 = `Needs ${t5}`;
  }
  const approvalStatusLabel = t3;
  const t4 = `tw-font-medium ${totalValueClass}`;
  const t5 = currentRating < 0 && "-";
  let t6;
  if ($[9] !== currentRating) {
    t6 = formatNumberWithCommas(Math.abs(currentRating));
    $[9] = currentRating;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <span className={t4}>{t5}{t6}</span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== displayWinningThreshold) {
    t8 = displayWinningThreshold !== null && <><span className="tw-font-medium tw-text-iron-500">/</span><span className="tw-font-medium tw-text-iron-50">{formatNumberWithCommas(displayWinningThreshold)}</span></>;
    $[15] = displayWinningThreshold;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = hasWinningThreshold ? drop.realtime_rating : drop.rating_prediction;
  const t10 = hasWinningThreshold ? "Realtime votes given" : undefined;
  let t11;
  if ($[17] !== currentRating || $[18] !== t10 || $[19] !== t9) {
    t11 = <DropVoteProgressing current={currentRating} projected={t9} tooltipLabel={t10} compact={true} />;
    $[17] = currentRating;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t7 || $[23] !== t8) {
    t12 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t7}{t8}{t11}</div>;
    $[21] = t11;
    $[22] = t7;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `total-rating-${drop.id}`;
  let t14;
  if ($[25] !== approvalStatusClass || $[26] !== approvalStatusLabel || $[27] !== hasWinningThreshold || $[28] !== votingLabel) {
    t14 = hasWinningThreshold && approvalStatusLabel !== null ? <span className={approvalStatusClass}>{approvalStatusLabel}</span> : <>{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</>;
    $[25] = approvalStatusClass;
    $[26] = approvalStatusLabel;
    $[27] = hasWinningThreshold;
    $[28] = votingLabel;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t13 || $[31] !== t14) {
    t15 = <span className="tw-cursor-help tw-whitespace-nowrap tw-font-normal tw-text-iron-400" data-tooltip-id={t13}>{t14}</span>;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const t16 = `total-rating-${drop.id}`;
  let t17;
  if ($[33] !== drop || $[34] !== ratingsData) {
    t17 = <VoteBreakdownTooltip drop={drop} ratingsData={ratingsData} />;
    $[33] = drop;
    $[34] = ratingsData;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <Tooltip id={t16} place="top" offset={8} opacity={1} positionStrategy="fixed" style={TOOLTIP_STYLES}>{t17}</Tooltip>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t12 || $[40] !== t15 || $[41] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t12}{t15}{t18}</div>;
    $[39] = t12;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
}